import { ForwardedRef, forwardRef, useImperativeHandle } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { useDerivedProps } from "hooks";
import { FieldValues, FormProvider, useForm } from "react-hook-form";

import {
  FormContainerControlledProps,
  FormContainerProps,
  FormContainerReference,
} from "./Form.types";

export type UseFormProps = ReturnType<typeof useForm>;

const FormContainerInner = <V extends FieldValues, R>(
  {
    schema,
    children,
    register,
    onSubmit,
    onError,
    ...innerProps
  }: FormContainerProps<V>,
  ref: ForwardedRef<R>
) => {
  const methods = useForm<V>({
    resolver: yupResolver(schema),
    ...innerProps,
  });

  useDerivedProps(() => {
    register?.(methods);
  }, register);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        methods,
      };
    }) as unknown as () => R,
    []
  );

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        autoComplete="off"
        onSubmit={
          onSubmit ? methods.handleSubmit(onSubmit, onError) : undefined
        }
      >
        {children}
      </form>
    </FormProvider>
  );
};

const FormContainer = forwardRef(FormContainerInner) as <V extends FieldValues>(
  props: FormContainerProps<V> & {
    ref?: ForwardedRef<FormContainerReference<V>>;
  }
) => ReturnType<typeof FormContainerInner>;

export const FormContainerControlled = <V extends FieldValues>({
  children,
  methods,
  onError,
  onSubmit,
}: FormContainerControlledProps<V>) => {
  return (
    <FormProvider {...methods}>
      <form
        noValidate
        autoComplete="off"
        onSubmit={
          onSubmit ? methods.handleSubmit(onSubmit, onError) : undefined
        }
      >
        {children}
      </form>
    </FormProvider>
  );
};

export default FormContainer;
